/**
 * 地区语言管理类
 * write by cursor
 */

/**
 * 标准地区语言码
 */
export enum StandardLocale {
  ZH_CN = 'zh-CN',
  ZH_TW = 'zh-TW',
  EN_US = 'en-US',
  KO_KR = 'ko-KR',
  JA_JP = 'ja-JP',
  PL_PL = 'pl-PL',
  FR_FR = 'fr-FR',
}

/**
 * 平台类型枚举
 * write by cursor
 */
export enum PlatformType {
  ALL = 'all',           // 所有平台（完整集合）
  IAM = 'iam',           // IAM平台
  DEVELOPER = 'developer', // 开发者平台
  OPEN = 'open',         // 开放平台
}

/**
 * 语言选项接口
 * write by cursor
 */
export interface LocaleOption {
  value: string;
  label: string;
}

/**
 * 语言标签映射 - 每种语言的原生名称
 * write by cursor
 */
const LOCALE_LABELS: Record<StandardLocale, string> = {
  [StandardLocale.ZH_CN]: '简体中文',
  [StandardLocale.ZH_TW]: '繁體中文',
  [StandardLocale.EN_US]: 'English',
  [StandardLocale.KO_KR]: '한국어',
  [StandardLocale.JA_JP]: '日本語',
  [StandardLocale.PL_PL]: 'Polski',
  [StandardLocale.FR_FR]: 'Français',
};

/**
 * 平台支持的语言集合
 * write by cursor
 */
const PLATFORM_LOCALES: Record<PlatformType, StandardLocale[]> = {
  [PlatformType.ALL]: Object.values(StandardLocale),
  [PlatformType.IAM]: [
    StandardLocale.ZH_CN,
    StandardLocale.ZH_TW,
    StandardLocale.EN_US,
    StandardLocale.JA_JP,
    StandardLocale.PL_PL,
    StandardLocale.FR_FR,
  ],
  [PlatformType.DEVELOPER]: [
    StandardLocale.ZH_CN,
    StandardLocale.EN_US,
    StandardLocale.KO_KR,
  ],
  [PlatformType.OPEN]: [
    StandardLocale.ZH_CN,
    StandardLocale.EN_US,
  ],
};

/**
 * 非标准语言码到标准语言码的映射
 */
const NON_STANDARD_TO_STANDARD_MAP: Record<string, StandardLocale> = {
  'zh': StandardLocale.ZH_CN,
  'en': StandardLocale.EN_US,
  'ko': StandardLocale.KO_KR,
  'ja': StandardLocale.JA_JP,
  'pl': StandardLocale.PL_PL,
  'fr': StandardLocale.FR_FR,
};

/**
 * 地区语言管理类 - 单例模式
 * write by cursor
 */
class LocaleManager {
  private static instance: LocaleManager;
  private readonly STORAGE_KEY = 'locale';
  private currentPlatform: PlatformType = PlatformType.ALL;
  
  /**
   * 私有构造函数，防止外部直接实例化
   */
  private constructor() {
    // 初始化时不做任何操作
  }

  /**
   * 获取单例实例
   */
  public static getInstance(): LocaleManager {
    if (!LocaleManager.instance) {
      LocaleManager.instance = new LocaleManager();
    }
    return LocaleManager.instance;
  }

  /**
   * 获取当前语言设置
   * 优先级：localStorage > 浏览器语言 > 英文(默认)
   * @returns 当前语言码
   */
  public getLocale(): string {
    // 1. 首先尝试从 localStorage 获取
    const localeFromStorage = localStorage.getItem(this.STORAGE_KEY);
    if (localeFromStorage) {
      return this.convertToStandardLocale(localeFromStorage);
    }
    
    // 2. 如果 localStorage 中没有，则尝试获取浏览器语言
    const browserLocale = this.getBrowserLocale();
    if (browserLocale) {
      // 将浏览器语言转换为标准语言码
      const standardBrowserLocale = this.convertToStandardLocale(browserLocale);
      // 保存到 localStorage 中以便下次使用
      localStorage.setItem(this.STORAGE_KEY, standardBrowserLocale);
      return standardBrowserLocale;
    }
    
    // 3. 如果以上都失败，则使用英文作为兜底
    return StandardLocale.EN_US;
  }

  /**
   * 获取浏览器语言设置
   * @returns 浏览器语言码
   */
  private getBrowserLocale(): string | null {
    // 尝试获取浏览器语言
    if (typeof window !== 'undefined' && window.navigator) {
      // 优先使用 navigator.languages
      if (window.navigator.languages && window.navigator.languages.length) {
        return window.navigator.languages[0];
      }
      
      // 然后尝试 navigator.language
      if (window.navigator.language) {
        return window.navigator.language;
      }
      
      // 兼容旧版浏览器
      // @ts-ignore
      if (window.navigator.browserLanguage) {
        // @ts-ignore
        return window.navigator.browserLanguage;
      }
      
      // @ts-ignore
      if (window.navigator.userLanguage) {
        // @ts-ignore
        return window.navigator.userLanguage;
      }
    }
    
    return null;
  }

  /**
   * 设置当前语言
   * @param locale 语言码
   */
  public setLocale(locale: string): void {
    // 如果是非标准语言码，先转换为标准语言码
    const standardLocale = this.convertToStandardLocale(locale);
    localStorage.setItem(this.STORAGE_KEY, standardLocale);
  }

  /**
   * 检查语言是否被指定平台支持
   * @param locale 标准语言码
   * @param platform 目标平台，默认为当前平台
   * @returns 如果支持返回该语言码，否则返回英文
   * write by cursor
   */
  private checkPlatformSupport(
    locale: StandardLocale,
    platform: PlatformType = this.currentPlatform
  ): StandardLocale {
    const platformLocales = PLATFORM_LOCALES[platform];
    if (platformLocales.includes(locale)) {
      return locale;
    }
    return StandardLocale.EN_US;
  }

  /**
   * 将非标准语言码转换为标准语言码
   * @param locale 语言码
   * @returns 标准语言码
   */
  public convertToStandardLocale(locale: string): string {
    if (!locale) {
      return StandardLocale.EN_US;
    }
    
    // 处理形如 'en-US' 的语言码，检查是否已经是标准语言码
    if (Object.values(StandardLocale).includes(locale as StandardLocale)) {
      return this.checkPlatformSupport(locale as StandardLocale);
    }
    
    // 处理形如 'en_US' 的语言码（下划线替换为连字符）
    const normalizedLocale = locale.replace('_', '-');
    if (Object.values(StandardLocale).includes(normalizedLocale as StandardLocale)) {
      return this.checkPlatformSupport(normalizedLocale as StandardLocale);
    }
    
    // 处理形如 'en-us' 的语言码（转换为大写）
    const parts = normalizedLocale.split('-');
    if (parts.length === 2) {
      const uppercasedLocale = `${parts[0]}-${parts[1].toUpperCase()}`;
      if (Object.values(StandardLocale).includes(uppercasedLocale as StandardLocale)) {
        return this.checkPlatformSupport(uppercasedLocale as StandardLocale);
      }
    }
    
    // 处理形如 'en' 的简单语言码
    const baseLocale = locale.split('-')[0].split('_')[0];
    const standardLocale = NON_STANDARD_TO_STANDARD_MAP[baseLocale];
    if (standardLocale) {
      return this.checkPlatformSupport(standardLocale);
    }
    
    // 如果无法转换，返回英文作为兜底
    console.warn(`unknown language code: ${locale} use default: ${StandardLocale.EN_US}`);
    return StandardLocale.EN_US;
  }

  /**
   * 获取语言的显示标签
   * @param locale 语言代码（可以是非标准的）
   * @param platform 平台类型（可选，默认为所有平台）
   * @returns 语言的显示标签
   * write by cursor
   */
  public getLocaleLabel(
    locale: string, 
    platform: PlatformType = PlatformType.ALL
  ): string {
    // 转换为标准语言代码
    const standardLocale = this.convertToStandardLocale(locale);
    
    // 检查该语言是否在指定平台的支持范围内
    const platformLocales = PLATFORM_LOCALES[platform];
    if (!platformLocales.includes(standardLocale as StandardLocale)) {
      console.warn(`Language ${standardLocale} is not supported in platform ${platform}`);
      // 返回默认语言的标签
      return LOCALE_LABELS[StandardLocale.EN_US];
    }
    
    // 返回语言的原生名称
    return LOCALE_LABELS[standardLocale as StandardLocale] || LOCALE_LABELS[StandardLocale.EN_US];
  }

  /**
   * 获取所有支持的语言选项列表
   * @param platform 平台类型，默认为所有平台
   * @returns 语言选项列表
   * write by cursor
   */
  public getLocaleOptions(
    platform: PlatformType = PlatformType.ALL
  ): LocaleOption[] {
    const locales = PLATFORM_LOCALES[platform];
    
    return locales.map(locale => ({
      value: locale,
      label: this.getLocaleLabel(locale, platform)
    }));
  }

  /**
   * 处理URL中的语言参数
   * 如果URL中包含locale参数，则保存到localStorage并清除URL中的参数
   * @param platform 平台类型，用于验证语言是否被平台支持
   * write by cursor
   */
  public handleUrlLocale(platform: PlatformType = PlatformType.ALL): void {
    // 保存当前平台类型
    this.currentPlatform = platform;

    if (typeof window === 'undefined') return;

    // 对 search 参数进行解码
    const decodedSearch = decodeURIComponent(window.location.search);
    const urlParams = new URLSearchParams(decodedSearch);
    const locale = urlParams.get('locale');

    if (locale) {
      // 转换为标准语言码（会自动检查平台支持）
      const standardLocale = this.convertToStandardLocale(locale);
      
      // 保存到 localStorage
      this.setLocale(standardLocale);
      console.log('Locale saved:', standardLocale);

      // 删除URL中的locale参数但不刷新页面
      const decodedHref = decodeURIComponent(window.location.href);
      const url = new URL(decodedHref);
      const hash = url.hash;
      url.searchParams.delete('locale');

      // 获取剩余的查询字符串
      let newSearch = url.searchParams.toString();
      newSearch = newSearch ? '?' + newSearch : '';

      // 构建新的URL (pathname + 剩余查询参数 + hash)
      const newUrl = url.origin + url.pathname + newSearch + hash;

      // 更新URL而不刷新页面
      window.history.replaceState({}, '', newUrl);
    }
  }
} 

export const getLocale = () => {
  return LocaleManager.getInstance().getLocale();
}

export const setLocale = (locale: string) => {
  LocaleManager.getInstance().setLocale(locale);
}

export const convertToStandardLocale = (locale: string) => {
  return LocaleManager.getInstance().convertToStandardLocale(locale);
}

/**
 * 获取语言的显示标签
 * @param locale 语言代码（可以是非标准的）
 * @param platform 平台类型（可选，默认为所有平台）
 * @returns 语言的显示标签
 * write by cursor
 */
export const getLocaleLabel = (
  locale: string, 
  platform: PlatformType = PlatformType.ALL
): string => {
  return LocaleManager.getInstance().getLocaleLabel(locale, platform);
}

/**
 * 获取所有支持的语言选项列表
 * @param platform 平台类型，默认为所有平台
 * @returns 语言选项列表，包含value和label
 * write by cursor
 */
export const getLocaleOptions = (
  platform: PlatformType = PlatformType.ALL
): LocaleOption[] => {
  return LocaleManager.getInstance().getLocaleOptions(platform);
}

/**
 * 处理URL中的语言参数
 * @param platform 平台类型，用于验证语言是否被平台支持
 * write by cursor
 */
export const handleUrlLocale = (platform: PlatformType = PlatformType.ALL): void => {
  LocaleManager.getInstance().handleUrlLocale(platform);
}
